/**
 * The template on the left, the document it produces on the right.
 *
 * Chosen over the block list and the document view on 2 September 2026, to be
 * refined against real use rather than further argument. The block list showed
 * an officer what they had written; it never showed them what it *did*, and
 * every defect found in the first live D01 was a defect of the second kind —
 * a paragraph that read perfectly well until you saw it beside the one above
 * it, or beside a real case's values.
 *
 * Clicking a paragraph in either pane selects it in both. The panel underneath
 * is the only place anything about a paragraph changes, so there is one place
 * to look and one place to be wrong.
 */

import { useMemo } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'

import { api } from '../api/client'
import type { TemplateDetail, TemplateBlockView } from '../api/client'
import { provenanceBadge } from '../lib/blocks'

/** The paragraph shapes a template can be started from.
 *
 * **Nothing is pre-seeded, and that is the point.** A noting carries no
 * letterhead; a despatched letter does. No single skeleton is right for every
 * document, and placing one puts a masthead on a document that must not have
 * one — which is how a template comes to carry a line this office does not
 * write and nobody notices until it is served.
 *
 * Each entry says what to create, not what it looks like: the shape is the
 * answer to the one question, and the words are the officer's.
 */
/**
 * What an administrator can put on a blank template.
 *
 * **Named for what the office writes, not for what the data model calls it.**
 * The one that had to change was `A paragraph with a gap`: it created a
 * `composed` block with **no gaps at all**, so the editor's next act was to warn
 * that the paragraph *"marks no gaps"* — a button that promises one thing,
 * delivers the opposite, and then complains about it.
 *
 * The two `[C]` entries are the same kind. What separates them is whether a
 * frame is seeded: with none, the model writes the paragraph; with a sentence
 * carrying `{{markers}}`, the model is never shown the sentence and fills only
 * the marked spans. That is the most consequential choice on this screen and it
 * was reachable only by knowing that an empty composed block behaves one way
 * and a marked one another.
 */
const PALETTE: {
  label: string
  provenance: 'O' | 'C'
  text: string
  role?: string
}[] = [
  { label: 'A heading', provenance: 'O', text: '' },
  { label: 'A paragraph the office always writes', provenance: 'O', text: '' },
  { label: 'A paragraph the model writes', provenance: 'C', text: '' },
  {
    label: 'A sentence with a gap the model fills',
    provenance: 'C',
    // Seeded with one marker, self-describing and obviously a placeholder, so
    // the block does not arrive already failing its own validation rule. The
    // administrator renames it to whatever the gap actually wants.
    text: '{{बदलने वाला भाग}}',
  },
  { label: 'The subject line', provenance: 'O', text: 'विषयः ' },
  { label: 'A reference line', provenance: 'O', text: 'संदर्भः ' },
  // **The factory's own furniture, which had no way in at all.** These four
  // paragraphs take their words from Settings, and `set_block_kind` refuses
  // `stationery` on a block that names no piece — so on a template built from a
  // blank page they were unreachable, and the first two lines of a real noting
  // had to be hand-typed as case blocks with markers nothing declared. Naming
  // the piece here is the only moment the name can be given.
  { label: 'The file-number line', provenance: 'O', text: '', role: 'file_number_line' },
  { label: 'The date line', provenance: 'O', text: '', role: 'date_line' },
  { label: 'The letterhead', provenance: 'O', text: '', role: 'letterhead' },
  { label: 'The approval ladder', provenance: 'O', text: '', role: 'signature_ladder' },
]

/** Draw the holes as holes.
 *
 * `{{marker}}` on the left and `⟦blank⟧` on the right are the same hole seen
 * twice — once as the officer wrote it, once as it prints. An officer who
 * cannot tell a hole from a word at a glance is reading the template for
 * defects with the defect hidden in the prose.
 */
function Words({ text }: { text: string }) {
  const parts = useMemo(() => text.split(/(\{\{[^{}]+\}\}|⟦[^⟧\n]{1,64}⟧)/g), [text])
  return (
    <span className="words">
      {parts.map((part, i) => {
        if (/^\{\{.+\}\}$/.test(part)) {
          return (
            <span key={i} className="marker">
              {part}
            </span>
          )
        }
        if (/^⟦.+⟧$/.test(part)) {
          return (
            <span key={i} className="blank">
              {part}
            </span>
          )
        }
        return <span key={i}>{part}</span>
      })}
    </span>
  )
}

function Paragraph({
  order,
  text,
  provenance,
  selected,
  onSelect,
  testId,
  flagged,
}: {
  order: number
  text: string
  provenance?: string | null
  selected: boolean
  onSelect: () => void
  testId: string
  flagged?: string | null
}) {
  // **Provenance travels onto every surface, including this one.** Neither
  // pane marked it, and the seeded library carries 68 `[D]` paragraphs — our
  // own unsourced drafts — plus 11 awaiting sign-off. An administrator reading
  // the right-hand pane, headed "what actually issues", saw them as settled
  // office text. That is stop list 8 reached by omission rather than by an
  // edit, and it is the pane most likely to be trusted.
  const badge = provenanceBadge(provenance)
  return (
    <button
      type="button"
      className={selected ? 'para on' : 'para'}
      aria-pressed={selected}
      onClick={onSelect}
      data-testid={testId}
    >
      <span className="n">{order}</span>
      {badge ? (
        <span className={`prov ${badge.tone}`} title={badge.title}>
          {badge.label}
        </span>
      ) : null}
      {/* **Which paragraphs have something to answer, without clicking
          each one.** The message itself is in the panel, beside the control
          that answers it; this is only the mark that says look here. */}
      {flagged ? (
        <span className="flag" title={flagged} aria-label="has a warning">
          !
        </span>
      ) : null}
      {text.trim() ? <Words text={text} /> : <span className="muted">(no words yet)</span>}
    </button>
  )
}

export function SplitEditor({
  detail,
  editable,
  language,
  tryOnCase,
  onChanged,
  selected,
  onSelect,
  warningsByBlock,
}: {
  detail: TemplateDetail
  editable: boolean
  language: 'hi' | 'en'
  tryOnCase: string
  onChanged: () => void
  selected: number | null
  onSelect: (order: number) => void
  warningsByBlock?: Map<number, string[]>
}) {
  const blocks = (detail.blocks ?? []).filter((b) => b.renders)

  // **The right pane re-renders when the words change, not when the version
  // does.** A draft keeps one version number across every edit made to it, so
  // keying on the version would leave the right pane showing the document as
  // it was several edits ago — which is the one thing this pane exists to
  // prevent an officer believing.
  //
  // **Everything the render depends on, and separated.** The key omitted
  // `instruction` and `gaps`, so saving a gap's instruction — the one edit
  // whose entire purpose is to change what the model writes — left this pane
  // showing the render from before it. The separators matter for the same
  // reason: without them adjacent fields run together and two different drafts
  // can produce one string.
  const signature = blocks
    .map((b) =>
      [
        b.order,
        b.text_hi,
        b.kind,
        b.style,
        b.instruction ?? '',
        JSON.stringify(b.gaps ?? []),
      ].join('|~|'),
    )
    .join('|::|')

  const preview = useQuery({
    queryKey: ['template-preview', detail.document_type, language, tryOnCase, signature],
    queryFn: () =>
      api.previewTemplateDocument(detail.document_type, {
        preflight: false,
        case_id: tryOnCase,
        // The pane is headed "what actually issues", so it must render the
        // language the officer is looking at. Without this the English tab
        // showed the Hindi document beside English paragraphs.
        language,
      } as never),
    enabled: Boolean(tryOnCase),
  })

  const rendered = new Map((preview.data?.blocks ?? []).map((b) => [b.order, b.text]))

  return (
    <div className="split" data-testid="split-editor">
      <div className="pane" data-testid="pane-template">
        <h3>The template — what you edit</h3>
        {blocks.length === 0 ? (
          <div className="empty" data-testid="template-empty">
            <p>
              This template has no paragraphs yet. Nothing has been placed for you: a noting
              carries no letterhead and a despatched letter does, so there is no skeleton that
              is right for both.
            </p>
            {editable ? (
              <Palette documentType={detail.document_type} onChanged={onChanged} />
            ) : null}
          </div>
        ) : (
          blocks.map((block) => (
            <Paragraph
              key={block.order}
              order={block.order}
              text={block.text_hi}
              provenance={block.provenance}
              selected={selected === block.order}
              onSelect={() => onSelect(block.order)}
              testId={`template-para-${block.order}`}
              flagged={warningsByBlock?.get(block.order)?.join(' · ') ?? null}
            />
          ))
        )}
      </div>

      <div className="pane" data-testid="pane-document">
        <h3>
          The document — what actually issues
          {preview.isFetching ? ' · rendering…' : ''}
        </h3>
        {!tryOnCase ? (
          <div className="empty" data-testid="preview-no-case">
            Choose a case above and this pane shows what this template produces for it. An
            instruction is judged by what it writes, not by how it reads.
          </div>
        ) : preview.error ? (
          <div className="empty warn" role="alert">
            {String((preview.error as Error).message)}
          </div>
        ) : (
          blocks.map((block) => (
            <Paragraph
              key={block.order}
              order={block.order}
              text={rendered.get(block.order) ?? ''}
              provenance={block.provenance}
              selected={selected === block.order}
              onSelect={() => onSelect(block.order)}
              testId={`document-para-${block.order}`}
            />
          ))
        )}
      </div>
    </div>
  )
}

/** The palette, offered where a template is empty and again under the list.
 *
 * Adding a paragraph is two acts on the server — create it, then answer what
 * it is — and they are done together here so a paragraph never spends a moment
 * on the page with its question unanswered.
 */
export function Palette({
  documentType,
  after,
  onChanged,
}: {
  documentType: string
  after?: number | null
  onChanged: () => void
}) {
  const add = useMutation({
    mutationFn: async (entry: (typeof PALETTE)[number]) => {
      const detail = await api.addTemplateBlock(documentType, {
        preflight: false,
        after: after ?? null,
        provenance: entry.provenance,
        text_hi: entry.text,
        instruction: '',
        org_block_role: entry.role ?? null,
      } as never)
      if (entry.provenance === 'O') return detail
      const added = pickAdded(detail.blocks ?? [], after ?? null)
      if (added === null) return detail
      return api.setBlockKind(documentType, {
        preflight: false,
        order: added,
        kind: 'composed',
      })
    },
    onSuccess: onChanged,
  })

  return (
    <div className="palette" data-testid="palette">
      {PALETTE.map((entry) => (
        <button
          key={entry.label}
          type="button"
          className="quiet"
          disabled={add.isPending}
          onClick={() => add.mutate(entry)}
          data-testid={`palette-${entry.provenance}-${entry.label.replace(/\W+/g, '-')}`}
        >
          + {entry.label}
        </button>
      ))}
      {add.error ? (
        <div className="small warn" role="alert">
          {String((add.error as Error).message)}
        </div>
      ) : null}
    </div>
  )
}

/** Which position the new paragraph landed at.
 *
 * `after: null` inserts FIRST and everything renumbers behind it, so "the
 * highest order" is wrong exactly where it looks most obviously right. The
 * server's own rule is the one read here: insert behind `after`, or at 1.
 */
function pickAdded(blocks: TemplateBlockView[], after: number | null): number | null {
  if (blocks.length === 0) return null
  return after === null ? 1 : after + 1
}
